import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import Base64Image from '../components/Base64Image';
import {
  RehabPageContainer,
  Section,
  SectionTitle,
  TextBlock,
  GalleryGrid,
  GalleryCard,
  GalleryTitle,
  GalleryDescription,
} from '../styles/RehabPageStyles';

const CACHE_KEY = 'rehab_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const RehabPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/rehab');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load rehab page.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <RehabPageContainer>
        <LoadingSpinner />
      </RehabPageContainer>
    );
  }

  if (error && !pageData) {
    return <RehabPageContainer>{error}</RehabPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const overviewText = pageData ? pageData.overviewText : null;
  const startDate = pageData ? pageData.startDate : null;
  const motivationText = pageData ? pageData.motivationText : null;
  const trainingText = pageData ? pageData.trainingText : null;
  const resultsText = pageData ? pageData.resultsText : null;
  const galleryImages =
    pageData && Array.isArray(pageData.galleryImages) ? pageData.galleryImages : [];

  const sortedGallery = [...galleryImages].sort((a, b) => {
    const ao = typeof a.displayOrder === 'number' ? a.displayOrder : 0;
    const bo = typeof b.displayOrder === 'number' ? b.displayOrder : 0;
    return ao - bo;
  });

  return (
    <RehabPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {(overviewText || startDate) && (
        <Section>
          <SectionTitle>ADDICT TO DISCIPLE PROGRAM</SectionTitle>
          <TextBlock>
            {startDate && (
              <p><strong>Program Start Date:</strong> {startDate}</p>
            )}
            {overviewText && <p>{overviewText}</p>}
          </TextBlock>
        </Section>
      )}

      {motivationText && (
        <Section>
          <SectionTitle>Motivation</SectionTitle>
          <TextBlock>{motivationText}</TextBlock>
        </Section>
      )}

      {trainingText && (
        <Section>
          <SectionTitle>Training</SectionTitle>
          <TextBlock>{trainingText}</TextBlock>
        </Section>
      )}

      {resultsText && (
        <Section>
          <SectionTitle>CHALLENGES</SectionTitle>
          <TextBlock>{resultsText}</TextBlock>
        </Section>
      )}

      {sortedGallery.length > 0 && (
        <Section>
          <SectionTitle>Pictures of our 1st Graduation program of the Addict to Disciple program.</SectionTitle>
          <GalleryGrid>
            {sortedGallery.map((item, index) => (
              <GalleryCard key={item._id || item.title || index}>
                {item.image && (
                  <Base64Image
                    image={item.image}
                    alt={item.title || 'Rehab program'}
                    style={{ width: '100%', borderRadius: 6, marginBottom: 6 }}
                  />
                )}
                {item.title && <GalleryTitle>{item.title}</GalleryTitle>}
                {item.description && (
                  <GalleryDescription>{item.description}</GalleryDescription>
                )}
              </GalleryCard>
            ))}
          </GalleryGrid>
        </Section>
      )}
    </RehabPageContainer>
  );
};

export default RehabPage;
